<template>
  <div v-if="!hideForRoutes.includes(route)" class="flex-grow-1 d-flex justify-content-center">
    <div class="global-search-container position-relative">
      <Input
        v-model="searchTerm"
        type="text"
        placeholder="Search here..."
        leadingIcon="mdi:magnify"
        :clearable="!!searchTerm"
        @input="debouncedGlobalSearch"
        @focus="showSearchDropdown = true"
        @blur="hideSearchDropdown"
        class="global-search-input-wrapper"
      />

      <div
        v-if="showSearchDropdown && searchTerm && globalSearch && globalSearch.length"
        class="global-search-dropdown"
      >
        <div
          v-for="(result, idx) in globalSearch"
          :key="idx"
          class="global-search-item"
          @mousedown.prevent="goToSearchResult(result)"
        >
          <div class="search-item__icon">
            <Icon
              :icon="
                result.icon === 'user' || result.entity === 'contact'
                  ? 'mdi:account'
                  : result.icon === 'building' || result.entity === 'company'
                    ? 'mdi:office-building'
                    : result.icon === 'file' || result.entity === 'template'
                      ? 'mdi:file-document'
                      : 'mdi:circle'
              "
              width="20"
              height="20"
            />
          </div>
          <div class="search-item__content">
            <h6 class="search-item__title">
              {{ result.title || result.name || result.email || result.phoneNumber }}
            </h6>
            <small class="search-item__entity">
              {{ result.subtitle || result.entity }}
            </small>
          </div>
          <div class="search-item__arrow">
            <Icon icon="mdi:chevron-right" width="16" height="16" />
          </div>
        </div>
      </div>

      <div v-if="globalSearchIsLoading" class="global-search-loading">
        <Icon icon="mdi:loading" width="16" height="16" class="spinning" />
      </div>
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import { Icon, Input } from "@/components/ui";
import {
  COMPANY_OVERVIEW_ROUTE,
  CONTACT_OVERVIEW_ROUTE,
  SUBSCRIPTION_SUCCESS_ROUTE,
  SUBSCRIPTION_WALL_ROUTE,
  TEMPLATES_ROUTE,
} from "@/router/routes";
import { GLOBAL_SEARCH } from "@/store/action-types";

export default {
  name: "GlobalSearch",
  components: {
    Icon,
    Input,
  },
  props: {
    route: {
      type: String,
      required: true,
    },
    account: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      COMPANY_OVERVIEW_ROUTE,
      CONTACT_OVERVIEW_ROUTE,
      TEMPLATES_ROUTE,
      SUBSCRIPTION_WALL_ROUTE,
      SUBSCRIPTION_SUCCESS_ROUTE,
      showSearchDropdown: true,
      searchTerm: "",
      hideForRoutes: [SUBSCRIPTION_WALL_ROUTE, SUBSCRIPTION_SUCCESS_ROUTE],
    };
  },
  computed: {
    globalSearch() {
      return this.$store.getters.globalSearch.data;
    },
    globalSearchIsLoading() {
      return this.$store.getters.globalSearch.isLoading;
    },
  },
  methods: {
    debouncedGlobalSearch: debounce(function () {
      if (this.searchTerm && this.searchTerm.length > 1) {
        this.$store.dispatch(GLOBAL_SEARCH, {
          searchTerm: this.searchTerm,
          organizationId: this.account.organizationId,
        });
      }
    }, 400),
    hideSearchDropdown() {
      setTimeout(() => {
        this.showSearchDropdown = false;
      }, 200);
    },
    goToSearchResult(result) {
      if (result && result.route) {
        this.$router.push(result.route);
      } else if (result?.entity === "contact") {
        this.$router.push({
          name: CONTACT_OVERVIEW_ROUTE,
          params: { id: result.id, name: result.title || result.name },
        });
      } else if (result?.entity === "company") {
        this.$router.push({
          name: COMPANY_OVERVIEW_ROUTE,
          params: { id: result.id },
        });
      } else if (result?.entity === "template") {
        this.$router.push({ name: TEMPLATES_ROUTE });
      }
      this.showSearchDropdown = false;
      this.searchTerm = "";
    },
  },
};
</script>

<style scoped>
.global-search-container {
  flex: 1 1 200px;
  min-width: 140px;
  max-width: 320px;
  margin-left: 8px;
  margin-right: 8px;
}

@media (min-width: 1500px) {
  .global-search-container {
    max-width: 360px;
    margin-left: 12px;
    margin-right: 12px;
  }
}

@media (max-width: 1100px) {
  .global-search-container {
    margin-left: 4px;
    margin-right: 4px;
  }
}

.global-search-input-wrapper {
  width: 100%;
}

.global-search-input-wrapper :deep(.ui-input__field) {
  background: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  height: 32px !important;
  padding-left: 28px !important;
  transition: all 0.2s ease;
}

.global-search-input-wrapper :deep(.ui-input__field:hover) {
  border-color: #9ca3af;
  box-shadow: 0px 1px 3px 0px rgba(0, 0, 0, 0.08);
}

.global-search-input-wrapper :deep(.ui-input__field:focus) {
  border-color: #3e3efe;
  box-shadow:
    0 0 0 3px rgba(62, 62, 254, 0.08),
    0px 1px 3px 0px rgba(0, 0, 0, 0.1);
}

.global-search-input-wrapper :deep(.ui-input__icon) {
  color: #6b7280;
}

.global-search-input-wrapper :deep(.ui-input__field:focus ~ .ui-input__leading .ui-input__icon) {
  color: #3e3efe;
}

.global-search-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  width: 100%;
  background: #ffffff;
  border: 1px solid #e4e4e7;
  border-radius: 12px;
  box-shadow:
    0px 4px 6px -1px rgba(0, 0, 0, 0.1),
    0px 2px 4px -1px rgba(0, 0, 0, 0.06),
    0px 0px 0px 1px rgba(0, 0, 0, 0.05);
  z-index: 2000;
  max-height: 320px;
  overflow-y: auto;
  padding: 6px;
}

.global-search-dropdown::-webkit-scrollbar {
  width: 6px;
}
.global-search-dropdown::-webkit-scrollbar-track {
  background: transparent;
}
.global-search-dropdown::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}
.global-search-dropdown::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

.global-search-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.15s ease;
  margin-bottom: 2px;
}
.global-search-item:last-child {
  margin-bottom: 0;
}
.global-search-item:hover {
  background: #f3f4f6;
}
.global-search-item:active {
  background: #e5e7eb;
  transform: scale(0.98);
}

.search-item__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f3f4f6;
  color: #3e3efe;
  flex-shrink: 0;
  transition: all 0.15s ease;
}
.global-search-item:hover .search-item__icon {
  background: #ebeaff;
  transform: scale(1.05);
}

.search-item__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.search-item__title {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.search-item__entity {
  font-size: 0.75rem;
  color: #6b7280;
  text-transform: capitalize;
  font-weight: 400;
}

.search-item__arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  opacity: 0;
  transition: all 0.15s ease;
  flex-shrink: 0;
}
.global-search-item:hover .search-item__arrow {
  opacity: 1;
  color: #3e3efe;
  transform: translateX(2px);
}

.global-search-loading {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 3;
  color: #3e3efe;
  pointer-events: none;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
